import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import {
  GenericTableColumn,
  GenericTableColumnTheme,
  GenericTableRowMeta,
  GENERIC_TABLE_COMPONENT_DEFAULT_CONFIG,
  GenericTableConfig,
} from '@pso-ui/generic-table/generic-table.model';
import { SelectionModel } from '@angular/cdk/collections';

@Component({
  selector: 'app-generic-table',
  templateUrl: './generic-table.component.html',
  styleUrls: ['./generic-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericTableComponent<T> {
  @Input() data: T[];
  @Input() dropArea: any;
  @Input() rowHighlightField: string | undefined;
  @HostBinding('class')
  @Input()
  theme: GenericTableColumnTheme = GenericTableColumnTheme.FILLED;

  get selection(): SelectionModel<T> {
    return this._selection;
  }

  @Input()
  set selection(value: SelectionModel<T>) {
    this._selection = value;
    this.columns = this._columns;
  }

  get columns(): GenericTableColumn[] {
    return this._columns;
  }

  @Input()
  set columns(value: GenericTableColumn[]) {
    if (!value) {
      return;
    }
    if (this._selection) {
      this.visibleColumns = ['select', ...value.map((c) => c.getName())];
    } else {
      this.visibleColumns = value.map((c) => c.getName());
    }
    this._columns = value;
  }

  private _config: GenericTableConfig = GENERIC_TABLE_COMPONENT_DEFAULT_CONFIG;

  @Input() set config(value: Partial<GenericTableConfig>) {
    this._config = {
      ...GENERIC_TABLE_COMPONENT_DEFAULT_CONFIG,
      ...value,
    };
  }

  get config(): GenericTableConfig {
    return this._config;
  }

  private _columns: GenericTableColumn[];
  private _selection: SelectionModel<T>;
  visibleColumns: string[];
  selectColumnWidthInPx = 40;
  protected readonly GenericTableColumn = GenericTableColumn;
  protected readonly GenericTableRowMeta = GenericTableRowMeta;

  collectiveCheckboxChanged(checked: boolean, data: T[], checkbox: CheckboxView): void {
    if (this.isCurrentPagePartiallySelected(data) || !checked) {
      this._selection.deselect(...data);
    } else {
      this._selection.select(...data);
    }

    checkbox.checked = this.isCurrentPageFullySelected(data);
    checkbox.indeterminate = this.isCurrentPagePartiallySelected(data);
  }

  isCurrentPageFullySelected(data: T[]): boolean {
    return data?.length > 0 && this.getCurrentPageSelectedCount(data) === data.length;
  }

  isCurrentPagePartiallySelected(data: T[]): boolean {
    const selectedCount = this.getCurrentPageSelectedCount(data);
    return selectedCount > 0 && selectedCount < data?.length;
  }

  rowCheckboxChanged(checked: boolean, model: T): void {
    if (checked) {
      this._selection.select(model);
    } else {
      this._selection.deselect(model);
    }
  }

  public isHighlighted(item: any): boolean {
    if (!this.rowHighlightField || !item) {
      return false;
    }

    const pathParts = this.rowHighlightField.split('.');

    let currentItem = item;

    for (const part of pathParts) {
      if (currentItem && typeof currentItem === 'object' && part in currentItem) {
        currentItem = currentItem[part];
      } else {
        return false;
      }
    }

    return !!currentItem;
  }

  private getCurrentPageSelectedCount(data: T[]): number {
    return data?.filter((item: T) => this._selection.isSelected(item)).length ?? 0;
  }
}

interface CheckboxView {
  checked: boolean;
  indeterminate: boolean;
}
